/**
 * EmbeddedConsentGate
 *
 * Blocks embedded app access until user accepts Terms of Service and Privacy Policy.
 * Analog of ConsentGate.tsx but for embedded (Bitrix24) layout:
 * - Uses embedded session auth (not Clerk)
 * - Reads consent status from EmbeddedOrgContext (populated from cookie session)
 * - Submits consent to POST /api/v1/embedded/consent
 * - Uses raw fetch (not useEmbeddedAuthFetch) because CSRF header is needed
 *   and the user may not be fully authenticated yet (pending-consent flow)
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { MarketingConsentField } from './MarketingConsentField';
import { API_BASE_URL } from '@/lib/api/config';
import {
  awaitEmbeddedRecoveryOutcome,
  notifyEmbeddedSessionExpired,
} from '@/lib/embedded-session-recovery';
import {
  embeddedGateHeightClass,
  embeddedSessionAuthHeaders,
  embeddedSessionExpiredKey,
} from '@/lib/embedded-host';

interface EmbeddedConsentGateProps {
  children: ReactNode;
}

export function EmbeddedConsentGate({ children }: EmbeddedConsentGateProps) {
  const { t } = useTranslation(['onboarding', 'common', 'dashboard']);
  const {
    csrfToken,
    consentRequired,
    isLoading,
    error: sessionError,
    clearConsentRequired,
    refreshSession,
    userEmail,
    crmVendor,
  } = useEmbeddedOrg();
  const screenHeight = embeddedGateHeightClass(crmVendor, 'h-screen');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  // Free choice, never a precondition — and it renders at all only when we hold
  // a real address. A portal user under the minimal Bitrix24 scope has a
  // `@no-mailbox.invalid` placeholder, so they are simply not asked: there is
  // nothing to subscribe, and asking for an address here would need e-mail
  // verification first (an unverified one routes reports to a stranger).
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linkRel = `${'noopener'} ${'noreferrer'}`;

  // H9: focus management — move focus to first checkbox when consent gate appears
  const firstCheckboxRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (consentRequired) {
      firstCheckboxRef.current?.focus();
    }
  }, [consentRequired]);

  const handleGrantConsent = async () => {
    if (!csrfToken) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // C5: credentials: 'include' to match session validation fetch
      const res = await fetch(`${API_BASE_URL}/api/v1/embedded/consent`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          ...embeddedSessionAuthHeaders(),
        },
        body: JSON.stringify({
          acceptedTerms: true,
          acceptedPrivacy: true,
          acceptedMarketing,
        }),
      });

      // A gate left open past the session's hour answers 401: the same
      // recovery as every other embedded request (a Bitrix24 re-entry, or
      // `aelo:session-expired` to a message host), not a save error the user
      // can only retry into the same 401.
      if (
        res.status === 401 &&
        notifyEmbeddedSessionExpired() &&
        (await awaitEmbeddedRecoveryOutcome())
      ) {
        return;
      }
      if (!res.ok) {
        const data = (await res.json().catch(() => ({
          error: t('common:errors.unknown', 'Unknown error'),
        }))) as {
          error?: string;
        };
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // Consent just created the user's org membership and set session.userId
      // on the backend. Re-read the session so the freshly-assigned role (client
      // for the first user) lands in context — clearConsentRequired() alone only
      // hides the consent screen, leaving the stale pre-consent role (null),
      // which would gate the org owner behind AccessRequestGate.
      clearConsentRequired();
      await refreshSession();
    } catch (err) {
      // C4: use i18n key instead of leaking raw backend error message
      console.error('embedded_consent_grant_failed', err);
      setError(
        t('consentGate.failedToSave', 'Failed to submit consent. Please try again.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // The session failed to load and recovery could not repair it (no SDK, the
  // frame refused, the loop guard tripped). `consentRequired` stays null in that
  // case, so without this branch the gate below would spin forever on a session
  // that is never coming — the tab looked hung rather than broken, and the
  // layout that renders errors lives further down, past this very gate.
  if (sessionError && !isLoading) {
    return (
      <div
        className={`flex ${screenHeight} flex-col items-center justify-center gap-4 bg-bg px-4 text-center`}
        role="alert"
      >
        <p className="text-sm text-fg-muted">{t(embeddedSessionExpiredKey(crmVendor))}</p>
        <Button variant="outline" onClick={() => void refreshSession()}>
          {t('common:common.retry', 'Retry')}
        </Button>
      </div>
    );
  }

  // Loading state (EmbeddedOrgContext is still fetching session)
  if (isLoading || consentRequired === null) {
    return (
      // H6: accessible loading state
      <div
        className={`flex ${screenHeight} items-center justify-center bg-bg`}
        role="status"
        aria-label={t('common:common.loading', 'Loading...')}
      >
        <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-fg-muted" />
      </div>
    );
  }

  // Consent not required — render children
  if (!consentRequired) {
    return <>{children}</>;
  }

  // Consent modal
  // H9: dialog role + aria-modal + aria-labelledby; M7: bg-bg-void
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
      className={`flex ${screenHeight} items-center justify-center bg-bg-void p-4`}
    >
      <Card className="w-full max-w-md bg-bg-elevated">
        <CardHeader>
          {/* H9: id for aria-labelledby */}
          <CardTitle id="consent-title" className="text-fg">
            {t('consent.title', 'Terms & Privacy')}
          </CardTitle>
          <p className="text-sm text-fg-muted">
            {t(
              'consent.description',
              'Please review and accept our Terms of Service and Privacy Policy to continue.'
            )}
          </p>
        </CardHeader>
        <CardContent>
          {/* M9: form element with onSubmit */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleGrantConsent();
            }}
            className="space-y-4"
          >
            {/* M8: min-h-11 touch targets on checkbox rows */}
            <div className="flex min-h-11 items-start gap-3">
              {/* H9: ref on first checkbox for focus management */}
              <Checkbox
                ref={firstCheckboxRef}
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              />
              <label htmlFor="terms" className="cursor-pointer text-sm text-fg">
                {t('consent.acceptTerms', 'I accept the')}{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel={linkRel}
                  className="text-primary underline hover:text-primary"
                >
                  {t('consent.termsOfService', 'Terms of Service')}
                </a>
              </label>
            </div>

            {/* M8: min-h-11 touch target */}
            <div className="flex min-h-11 items-start gap-3">
              <Checkbox
                id="privacy"
                checked={acceptedPrivacy}
                onCheckedChange={(checked) => setAcceptedPrivacy(checked === true)}
              />
              <label htmlFor="privacy" className="cursor-pointer text-sm text-fg">
                {t('consent.acceptPrivacy', 'I accept the')}{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel={linkRel}
                  className="text-primary underline hover:text-primary"
                >
                  {t('consent.privacyPolicy', 'Privacy Policy')}
                </a>
              </label>
            </div>

            <MarketingConsentField
              email={userEmail}
              checked={acceptedMarketing}
              onCheckedChange={setAcceptedMarketing}
              id="marketing-consent-embedded"
            />

            {/* H8: ARIA live region for errors — always rendered */}
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {error && (
                <div className="rounded-lg border border-danger bg-danger-dim p-3">
                  <p className="text-sm text-danger-fg">{error}</p>
                </div>
              )}
            </div>

            {/* Submit button with aria-busy + loading text */}
            <Button
              type="submit"
              disabled={!acceptedTerms || !acceptedPrivacy || isSubmitting}
              aria-busy={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {isSubmitting
                ? t('consentGate.saving', 'Submitting...')
                : t('consent.accept', 'Accept and Continue')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
